// employee-by-type-table.tsx — Tickets by employee and type group processing table.
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { TicketsByEmployeeAndType, TicketTypeGroup } from '@/api/tickets-statistics';

const TYPE_GROUP_LABEL: Record<TicketTypeGroup, string> = {
  support_request: 'Support',
  error_service:   'Error',
  service_request: 'Svc Req',
  service:         'Service',
  meeting:         'Meeting',
  change:          'Change',
  internal:        'Internal',
};

const ALL_GROUPS: TicketTypeGroup[] = [
  'support_request',
  'error_service',
  'service_request',
  'service',
  'meeting',
  'change',
  'internal',
];

function countForGroup(row: TicketsByEmployeeAndType, group: TicketTypeGroup): number {
  return row.ticket_counts.find((c) => c.ticket_type_group === group)?.total_tickets ?? 0;
}

function buildColumns(presentGroups: TicketTypeGroup[]): ColumnDef<TicketsByEmployeeAndType, unknown>[] {
  const typeGroupCols: ColumnDef<TicketsByEmployeeAndType, unknown>[] = presentGroups.map((g) => ({
    id: g,
    header: TYPE_GROUP_LABEL[g] ?? g,
    accessorFn: (row) => countForGroup(row, g),
    cell: ({ row }) => {
      const n = countForGroup(row.original, g);
      return n > 0 ? n.toLocaleString('en-US') : <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
    },
  }));

  return [
    {
      id: 'employee_name',
      accessorKey: 'employee_name',
      header: 'Employee',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.employee_name}
        </span>
      ),
    },
    ...typeGroupCols,
    {
      id: 'total_tickets',
      accessorKey: 'total_tickets',
      header: 'Total',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {row.original.total_tickets.toLocaleString('en-US')}
        </span>
      ),
    },
  ];
}

interface Props {
  data: TicketsByEmployeeAndType[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function EmployeeByTypeTable({ data, isLoading, isError }: Props) {
  const rows = [...(data ?? [])].sort((a, b) => b.total_tickets - a.total_tickets);

  // Only include type groups that have at least one non-zero count across all rows.
  const presentGroups = ALL_GROUPS.filter((g) =>
    rows.some((r) => countForGroup(r, g) > 0),
  );

  const columns = buildColumns(presentGroups);

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>
          Tickets by Employee &amp; Type
        </CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load employee/type data" />
        ) : isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 36 }} />
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.employee_id)}
              emptyState={
                <EmptyState
                  title="No data"
                  description="No employee/type data in this date range."
                />
              }
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
